import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { api } from '../services/api';

interface ImpersonationContextType {
  isImpersonating: boolean;
  originalUser: any | null;
  impersonatedUser: any | null;
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => void;
  canManageUsers: boolean;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, login } = useAuth();
  const queryClient = useQueryClient();
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalUser, setOriginalUser] = useState<any | null>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<any | null>(null);

  // Check if current user can manage users - requires manage access to users component
  const canManageUsers = user?.is_system_admin || (
    user?.userComponentAccess?.some(uca =>
      uca.component_name === 'crm' && uca.access_level === 'manage'
    ) ?? false
  );

  // Transform user data to ensure camelCase field names
  const transformUserData = (user: any) => {
    if (!user) return null;
    return {
      ...user,
      firstName: user.firstName || user.first_name,
      lastName: user.lastName || user.last_name,
      // Keep original fields for backward compatibility
      first_name: user.first_name || user.firstName,
      last_name: user.last_name || user.lastName
    };
  };

  // Persisted shape is intentionally minimal — the full user object returned by
  // the backend can include all userCompanies/permissions and easily exceed the
  // localStorage quota. The banner only needs identity + company name.
  const toPersistableUser = (user: any) => {
    if (!user) return null;
    const firstName = user.firstName || user.first_name;
    const lastName = user.lastName || user.last_name;
    return {
      id: user.id,
      email: user.email,
      firstName,
      lastName,
      first_name: firstName,
      last_name: lastName,
      is_system_admin: user.is_system_admin,
      company: user.company?.name ? { name: user.company.name } : undefined
    };
  };

  // Load impersonation state from localStorage on mount
  useEffect(() => {
    const savedImpersonation = localStorage.getItem('impersonation_state');
    if (savedImpersonation && canManageUsers) {
      try {
        const { originalUser: savedOriginal, impersonatedUser: savedImpersonated } = JSON.parse(savedImpersonation);
        setOriginalUser(transformUserData(savedOriginal));
        setImpersonatedUser(transformUserData(savedImpersonated));
        setIsImpersonating(true);
      } catch (error) {
        console.error('Error loading impersonation state:', error);
        localStorage.removeItem('impersonation_state');
      }
    }
  }, [canManageUsers]);

  const startImpersonation = useCallback(async (userId: string) => {
    if (!canManageUsers) {
      throw new Error('You do not have permission to manage users');
    }

    try {
      const response = await api.post(`/impersonation/start/${userId}`);
      const { user: userToImpersonate, originalUser: originalUserData, token: oneTimeToken } = response.data.data;

      // Transform and set impersonation state
      const transformedOriginalUser = transformUserData(originalUserData);
      const transformedImpersonatedUser = transformUserData(userToImpersonate);

      setOriginalUser(transformedOriginalUser);
      setImpersonatedUser(transformedImpersonatedUser);
      setIsImpersonating(true);

      // Save to localStorage — keep payload minimal to avoid QuotaExceededError.
      // Backend user objects include nested userCompanies/permissions that can
      // be large; the persisted shape only needs to survive a page reload.
      const currentToken = localStorage.getItem('token');
      try {
        localStorage.setItem('impersonation_state', JSON.stringify({
          originalUser: toPersistableUser(transformedOriginalUser),
          impersonatedUser: toPersistableUser(transformedImpersonatedUser),
          originalToken: currentToken
        }));
      } catch (storageError) {
        // Quota errors here shouldn't break the impersonation flow — React
        // state is already set, so the session works for the current tab.
        // We just lose the ability to restore on reload.
        console.warn('Could not persist impersonation_state to localStorage:', storageError);
      }

      // Switch token
      if (oneTimeToken) {
        // Update AuthContext state immediately to reflect new user
        // This triggers the app to behave as the impersonated user
        await login(oneTimeToken);

        // Clear all cached queries to ensure we don't show stale data (e.g. "My Assignments")
        queryClient.clear();
      }
    } catch (error) {
      console.error('Error starting impersonation:', error);
      throw error;
    }
  }, [canManageUsers, login, queryClient]);

  const stopImpersonation = useCallback(async () => {
    try {
      // Notify backend about stopping impersonation
      await api.post('/impersonation/stop');
    } catch (error) {
      console.error('Error notifying backend about impersonation stop:', error);
      // Continue with local cleanup even if backend call fails
    }

    // Restore original token
    const savedState = localStorage.getItem('impersonation_state');
    if (savedState) {
      try {
        const { originalToken } = JSON.parse(savedState);
        if (originalToken) {
          // Restore original user state in AuthContext
          await login(originalToken);
          // Clear cache again to remove impersonated data
          queryClient.clear();
        }
      } catch (e) {
        console.error('Error restoring token', e);
      }
    }

    setIsImpersonating(false);
    setOriginalUser(null);
    setImpersonatedUser(null);
    localStorage.removeItem('impersonation_state');
  }, [login, queryClient]);

  const value = useMemo(() => ({
    isImpersonating,
    originalUser,
    impersonatedUser,
    startImpersonation,
    stopImpersonation,
    canManageUsers
  }), [isImpersonating, originalUser, impersonatedUser, startImpersonation, stopImpersonation, canManageUsers]);

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};


export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};
